import React, { useEffect, useMemo, useRef, useState } from "react";
import M from "materialize-css";
import {
  CircleMarker,
  GeoJSON,
  MapContainer,
  Polyline,
  Popup,
  TileLayer,
  Tooltip,
  useMap,
} from "react-leaflet";
import { toPng } from "html-to-image";
import "leaflet/dist/leaflet.css";
import usStatesGeoJson from "./data/us-states.json";
import canadaRegionsGeoJson from "./data/canada-regions.json";
import mexicoRegionsGeoJson from "./data/mexico-regions.json";
import {
  getRoadTripMapTileConfig,
  RoadTripAppearanceSettings,
  resolveTripLineColor,
} from "./appearance";
import {
  buildStateCoverage,
  formatNumber,
  getCategoryLabel,
  getTripRenderCoordinates,
  getTripRegionCodes,
  getTripRegionNames,
  normalizeStateCode,
  REGION_NAME_BY_CODE,
  STATE_NAME_BY_CODE,
  summarizeStateCompletion,
  summarizeRoadTrips,
} from "./roadTripUtils";
import { loadAppearanceSettings } from "./storage";
import { RoadTrip, RoadTripCategory, RoadTripShowcaseConfig } from "./types";
import { useResolvedRoadTrips } from "./useResolvedRoadTrips";
import "./RoadTrips.css";

type TripVisibilityFilter = "all" | RoadTripCategory;

interface RoadTripShowcaseProps extends RoadTripShowcaseConfig {
  appearanceSettings?: RoadTripAppearanceSettings;
  autoResolveRoutes?: boolean;
  className?: string;
  headerActions?: React.ReactNode;
  compactSummary?: boolean;
  mapFirst?: boolean;
  showImageExport?: boolean;
  showBreakdowns?: boolean;
  showFilter?: boolean;
  showHeader?: boolean;
  showHero?: boolean;
  showHint?: boolean;
}

const DEFAULT_CENTER: [number, number] = [39.5, -98.35];
const DEFAULT_ZOOM = 4;
const DEFAULT_US_BOUNDS: [[number, number], [number, number]] = [
  [24.396308, -124.848974],
  [49.384358, -66.885444],
];

const FILTER_OPTIONS: { label: string; value: TripVisibilityFilter }[] = [
  { label: "All Trips", value: "all" },
  { label: "Trips Taken", value: "taken" },
  { label: "Trip Wishlist", value: "wishlist" },
];

const REGIONAL_BOUNDARY_COLLECTIONS = [
  usStatesGeoJson,
  canadaRegionsGeoJson,
  mexicoRegionsGeoJson,
];

function getFeatureRegionCode(feature: any): string | null {
  const rawCode =
    feature?.properties?.regionCode ?? feature?.properties?.name ?? "";
  return normalizeStateCode(String(rawCode));
}

function getFeatureRegionName(feature: any): string {
  return String(feature?.properties?.name ?? "Unknown region");
}

function MapResizerAndViewport({ trips }: { trips: RoadTrip[] }) {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    const invalidate = () => map.invalidateSize();

    invalidate();
    const timer = setTimeout(invalidate, 150);

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          invalidate();
        }
      },
      { threshold: 0.01 },
    );

    observer.observe(container);
    window.addEventListener("resize", invalidate);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      window.removeEventListener("resize", invalidate);
    };
  }, [map]);

  useEffect(() => {
    const points = trips.flatMap((trip) => getTripRenderCoordinates(trip));

    if (points.length >= 2) {
      map.fitBounds(points, {
        padding: [28, 28],
        maxZoom: 5,
      });
      return;
    }

    if (points.length === 1) {
      map.setView(points[0], 6);
      return;
    }

    map.fitBounds(DEFAULT_US_BOUNDS, { padding: [20, 20] });
  }, [map, trips]);

  return null;
}

function getStateLayerStyle(
  regionIdentifier: string,
  stateCoverage: ReturnType<typeof buildStateCoverage>,
  appearanceSettings: RoadTripAppearanceSettings,
) {
  const stateCode = normalizeStateCode(regionIdentifier);
  const coverage = stateCode ? stateCoverage[stateCode] : undefined;

  if (coverage?.taken && coverage?.wishlist) {
    return {
      color: appearanceSettings.takenStateBorderColor,
      weight: 1.5,
      fillColor: appearanceSettings.takenStateFillColor,
      fillOpacity: 0.58,
    };
  }

  if (coverage?.taken) {
    return {
      color: appearanceSettings.takenStateBorderColor,
      weight: 1.4,
      fillColor: appearanceSettings.takenStateFillColor,
      fillOpacity: 0.54,
    };
  }

  if (coverage?.wishlist) {
    return {
      color: appearanceSettings.wishlistStateBorderColor,
      weight: 1.4,
      dashArray: "6 6",
      fillColor: appearanceSettings.wishlistStateFillColor,
      fillOpacity: 0.45,
    };
  }

  return {
    color: appearanceSettings.neutralStateBorderColor,
    weight: 0.8,
    fillColor: appearanceSettings.neutralStateFillColor,
    fillOpacity: 0.5,
  };
}

function RoadTripShowcase({
  title,
  intro,
  trips,
  appearanceSettings,
  autoResolveRoutes = true,
  className = "",
  headerActions,
  compactSummary = false,
  mapFirst = false,
  showImageExport = false,
  showBreakdowns = true,
  showFilter = true,
  showHeader = true,
  showHero = true,
  showHint = true,
}: RoadTripShowcaseProps) {
  const [filter, setFilter] = useState<TripVisibilityFilter>("all");
  const [isExportingImage, setIsExportingImage] = useState(false);
  const mapShellRef = useRef<HTMLDivElement | null>(null);
  const { resolvedTrips, isResolvingRoutes } = useResolvedRoadTrips(
    trips,
    autoResolveRoutes,
  );
  const effectiveAppearanceSettings =
    appearanceSettings ?? loadAppearanceSettings();
  const mapTileConfig = useMemo(
    () => getRoadTripMapTileConfig(effectiveAppearanceSettings.mapStyle),
    [effectiveAppearanceSettings.mapStyle],
  );
  const isMinimalEmbeddedView =
    compactSummary && !showHeader && !showBreakdowns && !showFilter;

  const summary = useMemo(
    () => summarizeRoadTrips(resolvedTrips),
    [resolvedTrips],
  );
  const stateCompletion = useMemo(
    () => summarizeStateCompletion(summary.taken.statesVisited),
    [summary.taken.statesVisited],
  );
  const remainingStatesTooltip = useMemo(
    () =>
      stateCompletion.remainingStateCodes
        .map((stateCode) => STATE_NAME_BY_CODE[stateCode])
        .join(", "),
    [stateCompletion.remainingStateCodes],
  );
  const additionalVisitedPlaces = useMemo(
    () =>
      summary.taken.additionalRegionsVisited
        .map((regionCode) => REGION_NAME_BY_CODE[regionCode])
        .filter(Boolean),
    [summary.taken.additionalRegionsVisited],
  );
  const visibleTrips = useMemo(
    () =>
      resolvedTrips.filter(
        (trip) => filter === "all" || trip.category === filter,
      ),
    [filter, resolvedTrips],
  );
  const visibleStateCoverage = useMemo(
    () => buildStateCoverage(visibleTrips),
    [visibleTrips],
  );

  const groupedVisibleTrips = useMemo(
    () => ({
      taken: visibleTrips.filter((trip) => trip.category === "taken"),
      wishlist: visibleTrips.filter((trip) => trip.category === "wishlist"),
    }),
    [visibleTrips],
  );
  const handleExportMapImage = async () => {
    if (!mapShellRef.current) {
      return;
    }

    try {
      setIsExportingImage(true);
      const dataUrl = await toPng(mapShellRef.current, {
        cacheBust: true,
        pixelRatio: Math.max(window.devicePixelRatio || 1, 2.5),
        backgroundColor: "#f7f2ea",
      });
      const link = document.createElement("a");
      const titleSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

      link.download = `${titleSlug || "road-trip-atlas"}.png`;
      link.href = dataUrl;
      link.click();

      M.toast({
        html: "Saved a PNG snapshot of the current atlas view.",
        displayLength: 2400,
      });
    } catch (error) {
      M.toast({
        html: "Couldn't export the map image with this basemap right now.",
        displayLength: 3200,
      });
    } finally {
      setIsExportingImage(false);
    }
  };
  const summarySection = (
    <section
      className={`roadTrips__summaryGrid${
        compactSummary ? " roadTrips__summaryGrid--compact" : ""
      }`}
    >
      {isMinimalEmbeddedView ? (
        <>
          <article className="roadTrips__statCard">
            <span className="roadTrips__statLabel">Taken Trips</span>
            <strong className="roadTrips__statValue">
              {formatNumber(summary.taken.tripCount)}
            </strong>
          </article>
          <article className="roadTrips__statCard">
            <span className="roadTrips__statLabel">Miles</span>
            <strong className="roadTrips__statValue">
              {formatNumber(summary.taken.totalMiles)}
            </strong>
          </article>
          <article className="roadTrips__statCard">
            <span className="roadTrips__statLabel">Visited States</span>
            <strong className="roadTrips__statValue">
              {formatNumber(summary.taken.statesVisited.length)}
            </strong>
          </article>
        </>
      ) : (
        <>
          <article className="roadTrips__statCard">
            <span className="roadTrips__statLabel">Trips Taken</span>
            <strong className="roadTrips__statValue">
              {formatNumber(summary.taken.tripCount)}
            </strong>
            <span className="roadTrips__statMeta">
              {formatNumber(summary.taken.totalMiles)} miles logged
            </span>
          </article>
          <article className="roadTrips__statCard">
            <span className="roadTrips__statLabel">Trip Wishlist</span>
            <strong className="roadTrips__statValue">
              {formatNumber(summary.wishlist.tripCount)}
            </strong>
            <span className="roadTrips__statMeta">
              {formatNumber(summary.wishlist.totalMiles)} planned miles
            </span>
          </article>
          <article className="roadTrips__statCard">
            <span className="roadTrips__statLabel">Visited States</span>
            <strong className="roadTrips__statValue">
              {formatNumber(summary.taken.statesVisited.length)}
            </strong>
            <span className="roadTrips__statMeta">
              Filled in from completed trips
            </span>
          </article>
          <article className="roadTrips__statCard">
            <span className="roadTrips__statLabel">
              All Covered U.S. States
            </span>
            <strong className="roadTrips__statValue">
              {formatNumber(summary.totalStatesVisited.length)}
            </strong>
            <span className="roadTrips__statMeta">
              Taken and wishlist combined
            </span>
          </article>
        </>
      )}
    </section>
  );
  const mapSection = (
    <section className="roadTrips__mapSection card-panel">
      <div className="roadTrips__sectionHeader">
        <div>
          <h2 className="roadTrips__sectionTitle">Trip Map</h2>
          {!isMinimalEmbeddedView ? (
            <p className="roadTrips__sectionCopy">
              Completed trips use solid lines, wishlist routes use dashed lines,
              and region shading updates with the active filter.
            </p>
          ) : undefined}
        </div>
        <div className="roadTrips__mapActions">
          {showFilter ? (
            <div className="roadTrips__filterRow" aria-label="Trip filters">
              {FILTER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  className={`roadTrips__filterButton${
                    filter === option.value
                      ? " roadTrips__filterButton--active"
                      : ""
                  }`}
                  type="button"
                  onClick={() => setFilter(option.value)}
                  aria-pressed={filter === option.value}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : null}
          {showImageExport ? (
            <button
              type="button"
              className="btn-flat roadTrips__exportButton"
              onClick={() => {
                void handleExportMapImage();
              }}
              disabled={isExportingImage}
              title="Save the current visible atlas view as a PNG"
            >
              <i className="material-icons tiny">download</i>
              {isExportingImage ? "Saving..." : "Save PNG"}
            </button>
          ) : null}
        </div>
      </div>

      <div className="roadTrips__legend" aria-label="Map legend">
        <span className="roadTrips__legendItem">
          <span
            className="roadTrips__legendSwatch"
            style={{ background: effectiveAppearanceSettings.takenLineColor }}
          />
          Trips taken
        </span>
        <span className="roadTrips__legendItem">
          <span
            className="roadTrips__legendSwatch"
            style={{
              background: `repeating-linear-gradient(90deg, ${effectiveAppearanceSettings.wishlistLineColor} 0, ${effectiveAppearanceSettings.wishlistLineColor} 7px, #ffffff 7px, #ffffff 12px)`,
              borderColor: effectiveAppearanceSettings.wishlistLineColor,
            }}
          />
          Wishlist trips
        </span>
      </div>

      {isResolvingRoutes ? (
        <div
          className="roadTrips__routeStatus"
          role="status"
          aria-live="polite"
        >
          <span className="roadTrips__routeSpinner" />
          Calculating exact routes...
        </div>
      ) : null}

      <div ref={mapShellRef} className="roadTrips__mapShell">
        <MapContainer
          {...({
            center: DEFAULT_CENTER,
            zoom: DEFAULT_ZOOM,
            minZoom: 3,
            style: { height: "100%", width: "100%" },
            scrollWheelZoom: true,
          } as any)}
        >
          <MapResizerAndViewport trips={visibleTrips} />
          <TileLayer
            {...({
              attribution: mapTileConfig.attribution,
              crossOrigin: "anonymous",
              url: mapTileConfig.url,
            } as any)}
          />
          {REGIONAL_BOUNDARY_COLLECTIONS.map((boundaryCollection, index) => (
            <GeoJSON
              key={`boundary-layer-${index}`}
              {...({
                data: boundaryCollection as any,
                style: (feature: any) =>
                  getStateLayerStyle(
                    feature?.properties?.regionCode ??
                      feature?.properties?.name ??
                      "",
                    visibleStateCoverage,
                    effectiveAppearanceSettings,
                  ),
                onEachFeature: (feature: any, layer: any) => {
                  const regionName = getFeatureRegionName(feature);
                  const regionCode = getFeatureRegionCode(feature);
                  const coverage = regionCode
                    ? visibleStateCoverage[regionCode]
                    : undefined;

                  let coverageLabel = "No currently visible trips";
                  if (coverage?.taken && coverage?.wishlist) {
                    coverageLabel = "Visited";
                  } else if (coverage?.taken) {
                    coverageLabel = "Visited";
                  } else if (coverage?.wishlist) {
                    coverageLabel = "Wishlist";
                  }

                  layer.bindTooltip(`${regionName}: ${coverageLabel}`);
                },
              } as any)}
            />
          ))}

          {visibleTrips.map((trip) => {
            const positions = getTripRenderCoordinates(trip);
            const color = resolveTripLineColor(
              trip.category,
              trip.lineColor,
              effectiveAppearanceSettings,
            );

            return (
              <React.Fragment key={trip.id}>
                <Polyline
                  {...({
                    positions,
                    pathOptions: {
                      color,
                      weight: trip.category === "taken" ? 4 : 3.5,
                      opacity: trip.category === "taken" ? 0.88 : 0.78,
                      dashArray:
                        trip.category === "wishlist" ? "10 10" : undefined,
                    },
                  } as any)}
                />
                {trip.waypoints.map((waypoint, index) => (
                  <CircleMarker
                    key={`${trip.id}-${waypoint.name}-${index}`}
                    {...({
                      center: [waypoint.latitude, waypoint.longitude],
                      radius: 5.5,
                      pathOptions: {
                        color,
                        fillColor: "#ffffff",
                        fillOpacity: 0.95,
                        weight: 2,
                      },
                    } as any)}
                  >
                    <Tooltip direction="top" offset={[0, -8]}>
                      {trip.name}: stop {index + 1}
                    </Tooltip>
                    <Popup>
                      <strong>{waypoint.name}</strong>
                      <br />
                      {trip.name}
                      {waypoint.notes ? (
                        <>
                          <br />
                          {waypoint.notes}
                        </>
                      ) : null}
                    </Popup>
                  </CircleMarker>
                ))}
              </React.Fragment>
            );
          })}
        </MapContainer>
      </div>
    </section>
  );

  return (
    <div className={`roadTrips ${className}`.trim()}>
      {showHeader && showHero ? (
        <section className="roadTrips__hero card-panel">
          <div className="roadTrips__sectionHeader">
            <div>
              <p className="roadTrips__eyebrow">Reusable Road Trip Component</p>
              <h1 className="roadTrips__title">{title}</h1>
              <p className="roadTrips__intro">{intro}</p>
              {showHint ? (
                <p className="roadTrips__hint">
                  Routes start from waypoint lines, then resolve to likely roads
                  each time the trip atlas is viewed.
                </p>
              ) : null}
            </div>
            {headerActions ? (
              <div className="roadTrips__heroActions">{headerActions}</div>
            ) : null}
          </div>
        </section>
      ) : showHeader ? (
        <div className="roadTrips__inlineHeader">
          <div className="roadTrips__sectionHeader">
            <div>
              <h3 className="roadTrips__inlineTitle">{title}</h3>
              <p className="roadTrips__inlineCopy">{intro}</p>
            </div>
            {headerActions ? (
              <div className="roadTrips__heroActions">{headerActions}</div>
            ) : null}
          </div>
        </div>
      ) : null}

      {mapFirst ? mapSection : summarySection}
      {mapFirst ? summarySection : mapSection}

      <section className="roadTrips__completionCard card-panel">
        <div className="roadTrips__completionHeader">
          <div>
            <h2 className="roadTrips__sectionTitle">State Completion</h2>
            {!isMinimalEmbeddedView ? (
              <p className="roadTrips__sectionCopy">
                Based on completed trips only. Wishlist routes do not count
                toward visited-state completion.
              </p>
            ) : null}
          </div>
          <div
            className="roadTrips__completionBadge"
            title={
              stateCompletion.remainingCount > 0
                ? `States remaining: ${remainingStatesTooltip}`
                : "All tracked states are complete."
            }
          >
            {stateCompletion.remainingCount} remaining
          </div>
        </div>
        <div
          className="roadTrips__completionMeter"
          aria-label={`${stateCompletion.visitedCount} of ${stateCompletion.totalStates} states visited`}
        >
          <div
            className="roadTrips__completionFill"
            style={{
              width: `${stateCompletion.completionPercent}%`,
              background: `linear-gradient(90deg, ${effectiveAppearanceSettings.takenLineColor} 0%, ${effectiveAppearanceSettings.takenStateFillColor} 100%)`,
            }}
          />
        </div>
        <div className="roadTrips__completionStats">
          <span>
            {stateCompletion.visitedCount}/{stateCompletion.totalStates} states
            visited
          </span>
          <span>{stateCompletion.completionPercent}% complete</span>
        </div>
      </section>

      {additionalVisitedPlaces.length > 0 ? (
        <section className="roadTrips__completionCard card-panel">
          <div className="roadTrips__sectionHeader roadTrips__sectionHeader--stacked">
            <div>
              <h2 className="roadTrips__sectionTitle">
                Additional Visited Places
              </h2>
              {!isMinimalEmbeddedView ? (
                <p className="roadTrips__sectionCopy">
                  Non-U.S. provinces, territories, and states reached on
                  completed trips.
                </p>
              ) : null}
            </div>
          </div>
          <div className="roadTrips__stateList">
            {additionalVisitedPlaces.map((placeName) => (
              <span
                key={placeName}
                className="roadTrips__stateChip roadTrips__stateChip--taken"
              >
                {placeName}
              </span>
            ))}
          </div>
        </section>
      ) : null}

      {showBreakdowns ? (
        <>
          <section className="roadTrips__dataSection">
            <article className="roadTrips__categoryPanel card-panel">
              <h2 className="roadTrips__sectionTitle">Trips Taken</h2>
              <p className="roadTrips__categoryMeta">
                {formatNumber(summary.taken.tripCount)} trips
                {" • "}
                {formatNumber(summary.taken.totalMiles)} miles
                {" • "}
                {formatNumber(summary.taken.statesVisited.length)} states
              </p>
              <div className="roadTrips__stateList">
                {summary.taken.statesVisited.map((stateCode) => (
                  <span
                    key={stateCode}
                    className="roadTrips__stateChip roadTrips__stateChip--taken"
                  >
                    {STATE_NAME_BY_CODE[stateCode]}
                  </span>
                ))}
              </div>
            </article>
            <article className="roadTrips__categoryPanel card-panel">
              <h2 className="roadTrips__sectionTitle">Trip Wishlist</h2>
              <p className="roadTrips__categoryMeta">
                {formatNumber(summary.wishlist.tripCount)} trips
                {" • "}
                {formatNumber(summary.wishlist.totalMiles)} miles
                {" • "}
                {formatNumber(summary.wishlist.statesVisited.length)} states
              </p>
              <div className="roadTrips__stateList">
                {summary.wishlist.statesVisited.map((stateCode) => (
                  <span
                    key={stateCode}
                    className="roadTrips__stateChip roadTrips__stateChip--wishlist"
                  >
                    {STATE_NAME_BY_CODE[stateCode]}
                  </span>
                ))}
              </div>
            </article>
          </section>

          {(["taken", "wishlist"] as RoadTripCategory[]).map((category) => {
            const categoryTrips = groupedVisibleTrips[category];

            if (categoryTrips.length === 0) {
              return null;
            }

            return (
              <section key={category} className="roadTrips__tripSection">
                <div className="roadTrips__sectionHeader roadTrips__sectionHeader--stacked">
                  <div>
                    <h2 className="roadTrips__sectionTitle">
                      {getCategoryLabel(category)}
                    </h2>
                    <p className="roadTrips__sectionCopy">
                      {category === "taken"
                        ? "Completed drives with miles already counted in your running total."
                        : "Planned routes that stay visible without mixing into your visited-state total."}
                    </p>
                  </div>
                </div>
                <div className="roadTrips__tripGrid">
                  {categoryTrips.map((trip) => (
                    <article
                      key={trip.id}
                      className="roadTrips__tripCard card-panel"
                    >
                      <div className="roadTrips__tripHeading">
                        <div>
                          <h3 className="roadTrips__tripTitle">{trip.name}</h3>
                          {trip.description ? (
                            <p className="roadTrips__tripDescription">
                              {trip.description}
                            </p>
                          ) : null}
                        </div>
                        <div className="roadTrips__tripPills">
                          {trip.dateLabel ? (
                            <span
                              className={`roadTrips__tripPill roadTrips__tripPill--${trip.category}`}
                            >
                              {trip.dateLabel}
                            </span>
                          ) : null}
                          {trip.isShared ? (
                            <span className="roadTrips__tripPill roadTrips__tripPill--shared">
                              Shared
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="roadTrips__tripStats">
                        <span>{formatNumber(trip.miles)} miles</span>
                        <span>{trip.waypoints.length} waypoints</span>
                        <span>{getTripRegionCodes(trip).length} regions</span>
                      </div>
                      <p className="roadTrips__tripRoute">
                        {trip.waypoints
                          .map((waypoint) => waypoint.name)
                          .join(" -> ")}
                      </p>
                      <div className="roadTrips__stateList">
                        {getTripRegionNames(trip).map((stateName) => (
                          <span
                            key={`${trip.id}-${stateName}`}
                            className={`roadTrips__stateChip roadTrips__stateChip--${trip.category}`}
                          >
                            {stateName}
                          </span>
                        ))}
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            );
          })}
        </>
      ) : null}
    </div>
  );
}

export default RoadTripShowcase;
